/**
 * Centralized image sources for Blue Rock Concrete. Two kinds live here:
 *
 * 1. `REAL` / `REAL_ALT` / `HEADER` / `HERO_*` — actual Blue Rock project
 *    photos, served from `public/images`. Prefer these everywhere.
 * 2. `PHOTO` + `img()` — Unsplash PLACEHOLDERS, still used by the eight
 *    `/services/$slug` pages because the real set does not yet cover every
 *    service. Retire each id as a real photo replaces it.
 *
 * One helper, one map, so a photo changes in exactly one place.
 */

const UNSPLASH = "https://images.unsplash.com/photo-";

export function img(
  id: string,
  { w = 1200, h, q = 70 }: { w?: number; h?: number; q?: number } = {},
): string {
  const params = new URLSearchParams({
    auto: "format",
    fit: "crop",
    w: String(w),
    q: String(q),
  });
  if (h) params.set("h", String(h));
  return `${UNSPLASH}${id}?${params.toString()}`;
}

/** Raw Unsplash photo IDs, grouped by usage. */
export const PHOTO = {
  hero: "1646044447734-03341993d740", // bright finished concrete driveway curving through a green yard (daytime)
  driveway: "1771366260867-7e07094579d7", // fresh residential driveway
  patio: "1623625434531-d130448273c1", // pergola-covered patio
  stamped: "1738004185413-b66dc7b5d602", // stamped cobblestone pattern
  walkway: "1707313980602-398d13f6a9e4", // paver / sidewalk path
  retaining: "1769442263032-06c17a493bea", // retaining wall + walkway
  slab: "1694521787162-5373b598945c", // crew pouring a structural slab
  repair: "1781637202423-33ec5b47e52e", // sectioned slab / joints
  epoxy: "1780541027911-afb903996795", // garage / coated floor
  crew: "1531834685032-c34bf0d84c77", // crew on rebar (process / about)
  showcase: "1612352017570-ccc09253a973", // finished stamped patio in setting
  // Featured-work gallery (all distinct from the above)
  g1: "1775733924075-11e542629502", // modern concrete forecourt
  g2: "1678794792766-84eb99fe3010", // flagstone stamped finish
  g3: "1677909958693-cb5fc0b2b743", // wood-look stamped
  g4: "1779093932574-29dc4b98b199", // slate stamped texture
  g5: "1766128867984-0c12677a8178", // modern poured-concrete interior
  g6: "1715428326382-fc8201b31d92", // sculptural architectural concrete
  // /services detail blocks (d1–d8) — never reuse an image within one page;
  // /services itself only shows icon cards (no photos), so these are distinct.
  d1: "1628744448839-170bf324f27e", // bright home + fresh driveway (driveways)
  d2: "1694885090746-d90472e11c0e", // sunny backyard patio + outdoor kitchen (patios)
  d3: "1599353210656-34bf83754f80", // stamped brick-pattern texture close-up (stamped)
  d4: "1617408524056-1510660dcf74", // clean sidewalk through a lawn (walkways)
  d5: "1579724175242-0204ecac28cb", // poured concrete steps detail (steps/walls)
  d6: "1634580871828-8e6ad9190242", // crew pouring a slab (slabs)
  d7: "1630683924997-fe27050a0416", // worker grinding over rebar mesh (repair)
  d8: "1537923516370-f8da5df75e37", // glossy showroom floor with classic trucks (epoxy)
  // /process stage photos (p1–p7) — one per PROCESS_DETAIL_STEPS entry;
  // unique within the page.
  p1: "1770822662831-c361f15790ea", // worker in a safety vest writing on a clipboard/notebook at an outdoor job site (estimate)
  p2: "1503387837-b154d5074bd2", // hand drafting on a blueprint with a ruler on a wooden table (design)
  p3: "1773380498433-f6c511f82e40", // skid steer + crew laying a gravel base at a residential driveway excavation (excavation)
  p4: "1531834685032-c34bf0d84c77", // crew working a tall rebar cage grid (forming; fallback id, also used as `crew`)
  p5: "1745305945877-e155ee0e6052", // wet concrete pouring from a chute onto a screed board (pour)
  p6: "1773432114474-c1afba22a339", // worker troweling/finishing a fresh concrete slab (finishing)
  p7: "1783125127341-81112b795c23", // clean modern concrete driveway in front of a finished home (result)
} as const;

/**
 * REAL Blue Rock project photos, supplied by the client and converted to WebP
 * under `public/images`. These are not placeholders. Prefer them over a `PHOTO`
 * id anywhere a section can take one, and retire the matching placeholder when
 * a real photo covers the same service.
 */
export const REAL = {
  brickFacedSteps: "/images/brick-faced-steps.webp",
  redStampedWalkway: "/images/red-stamped-walkway.webp",
  stampedWalkwayDetail: "/images/stamped-walkway-detail.webp",
  stampedPatioFresh: "/images/stamped-patio-fresh.webp",
  exposedAggregateWalkway: "/images/exposed-aggregate-walkway.webp",
  slabPrepMesh: "/images/slab-prep-mesh.webp",
  stampedPatioGazebo: "/images/stamped-patio-gazebo.webp",
  stampedPatioGazeboWide: "/images/stamped-patio-gazebo-wide.webp",
} as const;

/** Alt text per real photo, so components never invent their own description. */
export const REAL_ALT: Record<keyof typeof REAL, string> = {
  brickFacedSteps: "Brick faced concrete entry steps during a rebuild",
  redStampedWalkway: "Red stamped concrete walkway with a brick pattern border",
  stampedWalkwayDetail: "Close up of an ashlar stamped concrete walkway",
  stampedPatioFresh: "A freshly finished stamped concrete patio behind a home",
  exposedAggregateWalkway: "An exposed aggregate walkway beside a brick home",
  slabPrepMesh: "Gravel base and wire mesh set for a backyard slab pour",
  stampedPatioGazebo: "A finished stamped concrete patio with a gazebo and seating",
  stampedPatioGazeboWide: "Wide view of a finished stamped concrete patio and gazebo",
};

/**
 * Page-header crops, one per route that uses `PageHeader`. Cut from the full-res
 * originals (not from the `REAL` gallery copies) at 1920x1080 desktop and
 * 900x760 mobile, so a header never upscales and a phone never downloads the
 * desktop file. Keyed by the route it heads.
 */
export const HEADER = {
  process: {
    src: "/images/slab-prep-mesh-header.webp",
    mobile: "/images/slab-prep-mesh-header-mobile.webp",
    alt: "A Blue Rock crew setting forms and wire mesh over a gravel base before a pour",
  },
  services: {
    src: "/images/stamped-walkway-detail-header.webp",
    mobile: "/images/stamped-walkway-detail-header-mobile.webp",
    alt: "Close up of an ashlar stamped concrete finish",
  },
  work: {
    src: "/images/stamped-patio-gazebo-header.webp",
    mobile: "/images/stamped-patio-gazebo-header-mobile.webp",
    alt: "A finished stamped concrete patio with a gazebo and seating",
  },
  reviews: {
    src: "/images/exposed-aggregate-walkway-header.webp",
    mobile: "/images/exposed-aggregate-walkway-header-mobile.webp",
    alt: "An exposed aggregate walkway beside a brick home",
  },
  // Reuses the /our-work crop: /quote renders no other photo, so cross-page
  // reuse is safe (see memory/lessons.md — uniqueness is a within-page rule).
  quote: {
    src: "/images/stamped-patio-gazebo-header.webp",
    mobile: "/images/stamped-patio-gazebo-header-mobile.webp",
    alt: "A finished stamped concrete patio with a gazebo and seating",
  },
  // Reuses the /reviews crop, distinct from /quote's; /contact renders no
  // other photo, so cross-page reuse is safe (uniqueness is a within-page rule).
  contact: {
    src: "/images/exposed-aggregate-walkway-header.webp",
    mobile: "/images/exposed-aggregate-walkway-header-mobile.webp",
    alt: "An exposed aggregate walkway beside a brick home",
  },
} as const;

/* Hero LCP variants — real project photo, pre-cropped to the exact display
   sizes (desktop wide, mobile portrait) so no transform service is involved.
   These stay the LCP element even now that the hero plays a film: the still
   paints first and the film fades in over it once the page has finished
   loading (see `HeroFilm` in `pages/home/Hero.tsx`). */
export const HERO_DESKTOP = "/images/hero-desktop.webp";
export const HERO_MOBILE = "/images/hero-mobile.webp";
export const HERO_ALT =
  "A finished stamped concrete patio poured and stamped by Blue Rock Concrete";

/* Hero film — one continuous camera flight across four services (slab prep,
   driveway pour, stamped patio, walkway), generated as a frame-locked chain so
   there are no cuts between them, and tail-dissolved into the head so the loop
   is invisible. No audio track: it is decorative, muted, and never controlled.
   Sources + prompts: `docs/hero-film.md`. */
export const HERO_VIDEO = "/video/hero.mp4";
export const HERO_VIDEO_MOBILE = "/video/hero-mobile.mp4";

/* OG / social preview (1200x630). JPEG on purpose: Facebook does not reliably
   render a WebP og:image. Must be absolute for social scrapers. */
export const SITE_ORIGIN = "https://bluerockconcreteco.com";
export const OG_IMAGE = `${SITE_ORIGIN}/images/og-preview.jpg`;
